// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';

export default function FPEPathArray({
  control,
  errors,
  inputDir,
  setValue,
  possiblePages
}) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `content.paths`
  });
  if (possiblePages === null) {
    return;
  }

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="pr-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="m-0 font-semibold">
                      Path {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Page ID"
                        instructions="Fill in the id of the Path page id"
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.paths.${index}.id`}
                        render={({ field }) => (
                          <FormControl
                            className="mt-16"
                            fullWidth
                            size="small"
                            error={!!errors.content?.paths?.[index]?.id}
                          >
                            <InputLabel id={`path-page-select-label-${index}`}>
                              Path Page
                            </InputLabel>
                            <Select
                              labelId={`path-page-select-label-${index}`}
                              id={`path-page-select-${index}`}
                              {...field}
                              label="Path Page"
                              value={
                                possiblePages.find(
                                  (page) => page.id === field.value
                                )
                                  ? field.value
                                  : ''
                              }
                            >
                              <MenuItem value="" disabled>
                                <em>Select a path page</em>
                              </MenuItem>
                              {possiblePages.map((page) => (
                                <MenuItem key={page.id} value={page.id}>
                                  {page.label} - {page.title}
                                </MenuItem>
                              ))}
                            </Select>
                            <FormHelperText>
                              {errors.content?.paths?.[index]?.id?.message}
                            </FormHelperText>
                          </FormControl>
                        )}
                      />
                    </Box>
                    <Box
                      className="flex items-center mt-32 p-0 border-t"
                      sx={{ backgroundColor: 'background.default' }}
                    >
                      {index !== 0 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      )}
                      {index !== fields.length - 1 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      )}
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:minus-circle
                        </BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Path
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
          onClick={() =>
            append({
              id: ''
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Path
          </span>
        </Button>
      </Box>
    </>
  );
}
